/**
 * Booking confirmation — terminal step, not part of the stepper.
 *
 * a11y:
 *   - Success banner has visible icon + heading + text (not color-only).
 *   - `.ics` download link has descriptive text "Add to calendar (iCal file)".
 *   - Confirmation # rendered in mono + amber background — accessible name
 *     includes the plain-text code via `aria-label`.
 */

import { useMemo } from 'react';
import { Button } from '@/components/primitives';
import type { BookingWizardState } from '../types';

export interface ConfirmationProps {
  state: BookingWizardState;
  onBookAnother: () => void;
  onReschedule: () => void;
  onCancel: () => void;
}

function buildIcsDataUri(state: BookingWizardState): string {
  if (!state.date || !state.time || !state.service) return '#';
  const [hh, mm] = state.time.split(':');
  const start = `${state.date.replace(/-/g, '')}T${(hh ?? '09').padStart(2, '0')}${(mm ?? '00').padStart(2, '0')}00`;
  const [sy, sm, sd] = state.date.split('-');
  const dur = state.service.durationMin;
  const endDate = new Date(Date.UTC(Number(sy), Number(sm) - 1, Number(sd), Number(hh ?? 9), Number(mm ?? 0) + dur));
  const pad = (n: number): string => `${n}`.padStart(2, '0');
  const end = `${endDate.getUTCFullYear()}${pad(endDate.getUTCMonth() + 1)}${pad(endDate.getUTCDate())}T${pad(endDate.getUTCHours())}${pad(endDate.getUTCMinutes())}00`;
  const summary = `PCB Appointment — ${state.service.title}`;
  const location = state.branch ? `${state.branch.branchName}, ${state.branch.addressLine1}, ${state.branch.city}, ${state.branch.state} ${state.branch.zip}` : '';
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Phelps County Bank//RAE v3//EN',
    'BEGIN:VEVENT',
    `UID:${state.confirmationNumber ?? 'rae-booking'}@mypcb.com`,
    `DTSTAMP:${start}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${summary}`,
    `LOCATION:${location}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

export function Confirmation({ state, onBookAnother, onReschedule, onCancel }: ConfirmationProps): JSX.Element {
  const icsHref = useMemo(() => buildIcsDataUri(state), [state]);

  if (!state.confirmationNumber) {
    return (
      <div className="rae-confirm">
        <p>No booking to confirm.</p>
      </div>
    );
  }

  const whenText = state.date && state.time
    ? `${new Date(`${state.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · ${state.time} (CDT)`
    : '';

  return (
    <div className="rae-confirm">
      <div className="rae-confirm__hero">
        <div className="rae-confirm__check" role="img" aria-label="Appointment confirmed successfully">✓</div>
        <h1 className="rae-confirm__h1">Done!</h1>
        <p><strong>Your appointment is booked.</strong></p>
        <p className="rae-confirm__sub">
          We've emailed a copy to <strong>{state.email}</strong>. Check your inbox (or spam folder, just in case).
        </p>
      </div>

      <section className="rae-confirm__instr" aria-labelledby="rae-instr-h2">
        <h2 id="rae-instr-h2">Instructions</h2>
        <p>Please bring these items to your appointment:</p>
        <ul>
          <li>Current government-issued photo ID (driver's license or passport)</li>
          <li>Funding source: check, debit card, or proof of external bank account</li>
          <li>Names, dates of birth, and Social Security Numbers of any beneficiaries you'd like to designate</li>
          <li>Your mobile phone (we'll set up online banking during the appointment)</li>
        </ul>
      </section>

      <section className="rae-confirm__detail-card" aria-labelledby="rae-detail-h2">
        <h2 id="rae-detail-h2" style={{ fontSize: 18, color: 'var(--pcb-blue-dark)', fontWeight: 500, marginBottom: 'var(--sp-8)', display: 'flex', alignItems: 'center', gap: 'var(--sp-5)' }}>
          <span aria-hidden>📅</span> Appointment Details
        </h2>

        <div className="rae-confirm__detail">
          <div className="k">Service</div>
          <div className="v"><strong>{state.service?.title}</strong> · {state.service?.durationMin} minutes</div>
        </div>
        <div className="rae-confirm__detail">
          <div className="k">Location</div>
          <div className="v">
            <strong>{state.branch?.branchName}</strong><br />
            {state.branch ? `${state.branch.addressLine1}, ${state.branch.city}, ${state.branch.state} ${state.branch.zip}` : ''}
          </div>
        </div>
        <div className="rae-confirm__detail">
          <div className="k">Date &amp; time</div>
          <div className="v"><strong>{whenText}</strong></div>
        </div>
        {state.officerDisplayName && (
          <div className="rae-confirm__detail">
            <div className="k">Staff</div>
            <div className="v">
              <span className="rae-staff-chip">
                <span className="rae-staff-chip__avatar" aria-hidden>{state.officerInitials}</span>
                <span><strong>{state.officerDisplayName}</strong> · FSR</span>
              </span>
            </div>
          </div>
        )}
        <div className="rae-confirm__detail">
          <div className="k">Confirmation #</div>
          <div className="v">
            <span className="rae-conf-code" aria-label={`Confirmation number ${state.confirmationNumber}`}>
              {state.confirmationNumber}
            </span>
          </div>
        </div>
      </section>

      <section className="rae-confirm__detail-card" aria-labelledby="rae-changes-h2">
        <h2 id="rae-changes-h2" style={{ fontSize: 17, color: 'var(--pcb-blue-dark)', fontWeight: 500, marginBottom: 'var(--sp-7)' }}>
          Changes to Your Appointment
        </h2>
        <div className="rae-change-grid">
          <button type="button" className="rae-change-btn" onClick={onReschedule}>
            <span aria-hidden>📅</span> Reschedule
          </button>
          <button type="button" className="rae-change-btn" onClick={onBookAnother}>
            <span aria-hidden>🔄</span> Update Method
          </button>
          <button type="button" className="rae-change-btn is-danger" onClick={onCancel}>
            <span aria-hidden>✕</span> Cancel
          </button>
        </div>
        <div style={{ marginTop: 'var(--sp-7)', fontSize: 12, color: 'var(--ink-500)', textAlign: 'center' }}>
          <strong style={{ color: 'var(--pcb-blue-dark)' }}>Self-service changes are available up to 24 hours before your appointment.</strong>
          {' '}After that, please call (573) 364-5202.
        </div>
      </section>

      <div style={{ textAlign: 'center', marginTop: 'var(--sp-10)', display: 'flex', gap: 'var(--sp-5)', justifyContent: 'center', flexWrap: 'wrap' }}>
        <a
          href={icsHref}
          download={`pcb-appointment-${state.confirmationNumber}.ics`}
          className="btn btn-primary"
          style={{ textDecoration: 'none' }}
          aria-label="Add to calendar (iCal file download)"
        >
          Add to calendar
        </a>
        <Button variant="ghost" onClick={onBookAnother}>Book another appointment</Button>
      </div>
    </div>
  );
}
